import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import QuickPricer from '../QuickPricer';
import { escapeClaimedElsewhere } from '../ui/SlideOver';

// ============================================================================
// Top-bar tool: Quick Price (Jaylen 2026-09-11: "put it in the top bar next
// to the bell… we will add tools like this here that follow you through to
// every page"). This is the first of the toolbar tools — a `$` button that
// drops the QuickPricer in a popover anchored to the top-right, on every
// page. Pattern for the next ones: a small trigger in App's header row,
// `open` state owned by App (so the P shortcut can toggle it), and the
// panel portaled to <body> so page scroll containers can't clip it.
//
// role="dialog" matters: App's page shortcuts (1-9 navigate) suspend while
// any dialog is open, so typing "235" into the rate field can't jump pages.
// ============================================================================

export default function QuickPriceTool({ open, onToggle, onClose }) {
    const panelRef = useRef(null);
    const btnRef = useRef(null);

    // Escape closes; click outside (but not on the trigger) closes.
    // The form-control popovers (TimePicker / DatePicker / Combobox) portal
    // OUTSIDE the panel and mark themselves `data-bbm-popover` — a click in
    // one, or Escape while one is open, belongs to the popover, not to us.
    // (Jaylen 2026-09-17: picking a start time closed the whole calculator.)
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (escapeClaimedElsewhere()) return;
            e.stopPropagation();
            onClose();
        };
        const onDown = (e) => {
            if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            if (e.target?.closest?.('[data-bbm-popover], .pac-container')) return;
            onClose();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
    }, [open, onClose]);

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={onToggle}
                aria-label={open ? 'Close Billing Calculator' : 'Billing Calculator'}
                aria-expanded={open}
                title="Billing Calculator (P)"
                className={`h-9 w-9 rounded-lg flex items-center justify-center font-mono text-[15px] font-bold transition-colors ${
                    open ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
            >
                $
            </button>
            {open && createPortal((
                // z-[60]: above the header's own dropdowns (z-50), below
                // modals (z-100) so a confirm can still sit on top of it.
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-label="Billing Calculator"
                    className="theme-fixed fixed z-[60] top-16 right-3 left-3 md:left-auto md:w-[min(1120px,calc(100vw-1.5rem))] max-h-[calc(100vh-5rem)] overflow-auto rounded-2xl"
                    style={{ animation: 'qp-drop var(--duration-popover, 180ms) var(--ease-out, ease-out)' }}
                >
                    <style>{`@keyframes qp-drop { from { opacity: 0; transform: translateY(-6px) scale(0.985); } to { opacity: 1; transform: none; } }`}</style>
                    <QuickPricer onClose={onClose} />
                </div>
            ), document.body)}
        </>
    );
}
